import { cookies, setCookie, removeCookie } from 'sharedKernel/cookies/behavior'
import { ICredLocalStorage } from './contracts/iCredLocalStorage'

export * from './contracts/iCredLocalStorage'

// Profile data that used to live inside the `_cred` cookie. Each entry of `profiles[]`
// costs ~420 bytes, so a user with 5+ profiles pushed `_cred` past the browser's ~4096
// byte per-cookie limit; the browser then dropped the whole cookie silently, the API
// interceptor found no token, and every request after a successful login went out
// anonymous (401). `_cred` now carries only identity/session ({id, token, email, name,
// role}); everything else lives here. See ADR-061.
const CRED_LOCAL_STORAGE_KEY = '_cred_localStorage'

// Cross-subdomain bridge. localStorage is per-origin, but a session deliberately spans
// two hosts — the shell (app-*) and the legacy (legacy-*) — which share cookies on the
// parent domain via REACT_APP_COOKIE_DOMAIN. Without a carrier, whichever app is reached
// through a SmartNav hop boots with a valid `_cred` cookie but no company/profiles, and
// menus, route guards and city-hall checks silently degrade. `_credShared` carries the
// same payload across the hop, capped so it can never reproduce the overflow it exists to
// fix: `profiles[]` is the only unbounded field, so it is the first thing dropped.
const CRED_BRIDGE_COOKIE_KEY = '_credShared'
const CRED_BRIDGE_MAX_BYTES = 3500

const encodedSize = (data: ICredLocalStorage): number => encodeURIComponent(JSON.stringify(data)).length

const writeBridgeCookie = (data: ICredLocalStorage): void => {
  if (encodedSize(data) <= CRED_BRIDGE_MAX_BYTES) {
    setCookie(CRED_BRIDGE_COOKIE_KEY, data)
    return
  }

  const { profiles, ...withoutProfiles } = data
  if (encodedSize(withoutProfiles) <= CRED_BRIDGE_MAX_BYTES) {
    setCookie(CRED_BRIDGE_COOKIE_KEY, withoutProfiles)
    return
  }

  removeCookie(CRED_BRIDGE_COOKIE_KEY)
}

const readLocalStorage = (): ICredLocalStorage | undefined => {
  const raw = localStorage.getItem(CRED_LOCAL_STORAGE_KEY)
  // Explicit null/empty check: `getItem` returns `string | null`, and a truthiness test on a
  // nullable string trips @typescript-eslint/strict-boolean-expressions.
  if (raw === null || raw === '') return undefined
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

export const setCredLocalStorage = (data: ICredLocalStorage): void => {
  localStorage.setItem(CRED_LOCAL_STORAGE_KEY, JSON.stringify(data))
  writeBridgeCookie(data)
}

export const getCredLocalStorage = (): ICredLocalStorage | undefined => {
  const local = readLocalStorage()
  const bridge = cookies.get(CRED_BRIDGE_COOKIE_KEY) as ICredLocalStorage | undefined

  // The bridge cookie wins whenever it exists: it is shared across the two hosts, so it is
  // the only copy guaranteed to belong to the current session. A localStorage blob on this
  // origin may have been written by a previous login (logout on the *other* host cannot
  // reach it), which is why it is only trusted for the same user id.
  if (bridge !== undefined) {
    if (bridge.profiles === undefined && local?.profiles !== undefined && local.id === bridge.id) {
      return { ...bridge, profiles: local.profiles }
    }
    return bridge
  }

  return local
}

export const removeCredLocalStorage = (): void => {
  localStorage.removeItem(CRED_LOCAL_STORAGE_KEY)
  removeCookie(CRED_BRIDGE_COOKIE_KEY)
}
